import { useMemo, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { Mail, FileText, Video, Clock, Check, X, ArrowRight } from 'lucide-react';
import api from '../../lib/api';
import { Invitation } from '../../types';
import { invitationUrlSegment } from '../../lib/slug';
import { useAuthStore } from '../../stores/authStore';
import { StatNumber, StatsGridSkeleton } from '../../components/ui/StatsLoading';
import InvitationNextSteps from '../../components/kol/InvitationNextSteps';

export default function KOLDashboard() {
  const queryClient = useQueryClient();
  const user = useAuthStore((state) => state.user);
  const [respondingId, setRespondingId] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const invQuery = useQuery({
    queryKey: ['kol', 'dashboard', 'invitations'],
    queryFn: async () => (await api.get('/invitations')).data as Invitation[],
    staleTime: 2 * 60 * 1000,
  });

  const invitations = invQuery.data ?? [];
  const refetching = invQuery.isFetching && !invQuery.isPending;

  const pendingInvitations = useMemo(
    () => invitations.filter((inv) => inv.status === 'PENDING'),
    [invitations]
  );
  const acceptedInvitations = useMemo(
    () => invitations.filter((inv) => inv.status === 'ACCEPTED'),
    [invitations]
  );

  const stats = [
    { title: 'Pending Invitations', value: pendingInvitations.length, icon: Mail, link: '/kol/invitations' },
    { title: 'Active campaigns', value: acceptedInvitations.length, icon: FileText, link: '/kol/invitations' },
    { title: 'Scripts', value: 0, icon: FileText, link: '/kol/scripts' },
    { title: 'Submissions', value: 0, icon: Video, link: '/kol/submissions' },
  ];

  const handleRespond = async (invitation: Invitation, status: 'ACCEPTED' | 'REJECTED') => {
    const slug = invitation.urlSlug || invitation.id;
    try {
      setRespondingId(invitation.id);
      await api.post(`/invitations/${encodeURIComponent(slug)}/respond`, { status });
      await queryClient.invalidateQueries({ queryKey: ['kol', 'dashboard', 'invitations'] });
      setNotice(status === 'ACCEPTED' ? 'Invitation accepted.' : 'Invitation declined.');
    } catch (error: unknown) {
      const err = error as { response?: { data?: { error?: string } } };
      setNotice(err.response?.data?.error || 'Failed to respond to invitation');
    } finally {
      setRespondingId(null);
    }
  };

  const featuredInvitation = pendingInvitations[0];
  const firstName = (user?.fullName || 'there').split(' ')[0];

  return (
    <div className="space-y-6">
      <div>
        <h1 className="page-title text-[1.65rem] sm:text-[1.85rem]">Good day, {firstName}</h1>
        <p className="text-glass-text-2 mt-1 text-sm">Your KOL portal — invitations, scripts, and submissions.</p>
      </div>

      {notice && (
        <div role="status" className="rounded-xl border border-glass bg-glass-2 px-4 py-3 text-sm text-glass-text">
          {notice}
        </div>
      )}

      {invQuery.isPending ? (
        <StatsGridSkeleton count={4} className="gap-4" />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {stats.map((stat) => {
            const Icon = stat.icon;
            return (
              <Link
                key={stat.title}
                to={stat.link}
                className="glass-card p-5 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
              >
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <p className="text-[0.7rem] uppercase tracking-[0.05em] font-medium text-glass-text-3">
                      {stat.title}
                    </p>
                    <div className="stat-display text-[1.85rem] text-glass-text mt-2 min-h-[2.25rem] flex items-center">
                      <StatNumber loading={refetching}>{stat.value}</StatNumber>
                    </div>
                  </div>
                  <div
                    className="w-10 h-10 rounded-[13px] flex items-center justify-center text-white shrink-0"
                    style={{
                      background:
                        'linear-gradient(135deg, rgb(var(--accent-rgb) / 0.9), rgb(var(--accent2-rgb) / 0.7))',
                    }}
                  >
                    <Icon className="w-5 h-5" />
                  </div>
                </div>
              </Link>
            );
          })}
        </div>
      )}

      {featuredInvitation ? (
        <div className="glass-card p-5 hover:-translate-y-0.5 transition-all duration-150">
          <div className="flex items-center justify-between mb-4">
            <h2 className="text-[0.95rem] font-semibold text-glass-text">Invitation Response</h2>
            <span className="status-pill status-pill-warn">Pending</span>
          </div>

          <div className="p-4 rounded-[14px] border border-glass bg-glass-2">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
              <div className="min-w-0 flex-1">
                <h3 className="font-semibold text-glass-text text-base">
                  {featuredInvitation.job?.title || 'Campaign invitation'}
                </h3>
                <p className="text-sm text-glass-text-2 mt-1">
                  {featuredInvitation.job?.brandOrg?.name || 'Brand partner'}
                </p>
                <div className="flex items-center gap-2 mt-2 text-xs text-glass-text-3">
                  <Clock className="w-3.5 h-3.5" />
                  Received {new Date(featuredInvitation.createdAt).toLocaleDateString()}
                </div>
                {featuredInvitation.job?.description && (
                  <p className="text-sm text-glass-text-2 mt-3 line-clamp-2">
                    {featuredInvitation.job.description}
                  </p>
                )}
                <InvitationNextSteps status={featuredInvitation.status} compact />
              </div>

              <div className="flex flex-col sm:items-end gap-2 shrink-0">
                <div className="flex gap-2">
                  <button
                    type="button"
                    className="btn-accent inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold disabled:opacity-60"
                    disabled={respondingId === featuredInvitation.id}
                    onClick={() => handleRespond(featuredInvitation, 'ACCEPTED')}
                  >
                    <Check className="w-4 h-4" />
                    Accept
                  </button>
                  <button
                    type="button"
                    className="btn-glass inline-flex items-center gap-1.5 px-4 py-2 text-sm font-semibold disabled:opacity-60"
                    disabled={respondingId === featuredInvitation.id}
                    onClick={() => handleRespond(featuredInvitation, 'REJECTED')}
                  >
                    <X className="w-4 h-4" />
                    Decline
                  </button>
                </div>
                <Link
                  to={`/kol/invitations/${encodeURIComponent(invitationUrlSegment(featuredInvitation))}`}
                  className="text-xs font-semibold text-accent2 inline-flex items-center gap-1"
                >
                  View details <ArrowRight className="w-3.5 h-3.5" />
                </Link>
              </div>
            </div>
          </div>

          {pendingInvitations.length > 1 && (
            <div className="mt-4 space-y-2">
              <p className="text-xs font-medium text-glass-text-3 uppercase tracking-[0.05em]">
                {pendingInvitations.length - 1} more pending
              </p>
              {pendingInvitations.slice(1, 4).map((invitation) => (
                <Link
                  key={invitation.id}
                  to={`/kol/invitations/${encodeURIComponent(invitationUrlSegment(invitation))}`}
                  className="flex items-center justify-between p-3 rounded-[14px] border border-glass bg-glass-2 hover:bg-glass-3 hover:-translate-y-0.5 hover:border-accent2/40 transition-all duration-150"
                >
                  <div className="min-w-0">
                    <div className="font-medium text-glass-text text-sm truncate">
                      {invitation.job?.title}
                    </div>
                    <div className="text-xs text-glass-text-3 truncate">
                      {invitation.job?.brandOrg?.name}
                    </div>
                  </div>
                  <span className="status-pill status-pill-warn shrink-0 ml-2">Pending</span>
                </Link>
              ))}
              {pendingInvitations.length > 4 && (
                <Link
                  to="/kol/invitations"
                  className="block text-center text-xs font-semibold text-accent2 pt-1"
                >
                  View all {pendingInvitations.length} invitations
                </Link>
              )}
            </div>
          )}
        </div>
      ) : (
        <div className="glass-card p-5 text-center">
          <p className="text-glass-text-2 text-sm">No pending invitations right now.</p>
          <Link to="/kol/invitations" className="btn-glass inline-flex mt-4 px-4 py-2 text-sm font-semibold">
            View invitation history
          </Link>
        </div>
      )}
    </div>
  );
}
